import {
  DropdownHeader,
  DropdownItem,
  DropdownOptionType,
} from '@ardoq/dropdown-menu';
import {
  BlocksViewNode,
  BlocksViewVisualState,
  Controller,
  Interactions,
  LayoutType,
} from '../types';
import {
  findDrawnGraphNode,
  hitTestGraphNodes,
  renderGraph,
} from '../visual/visual';
import {
  DropdownConfig,
  SettingsConfig,
  SettingsType,
} from '@ardoq/view-settings';
import { rectCenter, rectHeight, rectWidth } from '../misc/geometry';
import { clamp } from 'lodash';
import { isGroup } from '../viewModel$/util';
import { applyLayout } from '../layout/layout';
import { IconName } from '@ardoq/icons';
import {
  layoutSettingViewActionButton,
  layoutSettingViewActionOption,
} from './viewActions';
import {
  linkContextMenuOptions,
  nodeContextMenuOptions,
  rootContextMenuOptions,
} from './contextMenuOptions';
import {
  TrackedContextMenus,
  getTrackingFunction,
} from 'contextMenus/tracking';
import {
  getExpandOrCollapseGroupMenuItem,
  getZoomToFitContextMenuItem,
} from 'contextMenus/utils';
import {
  getWorkspaceMenu,
  toWorkspaceMenuState,
} from 'contextMenus/workspaceMenu';
import { transformComponentMenu } from './transformComponentMenu';
import { isWorkspace, ViewIds } from '@ardoq/api-types';
import { getMovePreview, getResizePreview } from './editPreview';
import { forceNodeCell, renderEditPreview } from '../visual/editPreview';
import { editApply } from '../layout/edit';
import { isMac } from '@ardoq/common-helpers';
import { maybeDispatchUrlIconClick } from '@ardoq/graph';
import {
  register as registerCanvasHitTest,
  unregister as unregisterCanvasHitTest,
  type ClientModelHitTest,
  type ClientNodeLocator,
} from 'tabview/canvasRendering/canvasHitTestRegistry';
import { NodeHit, updateHover } from './interactions';
import { renderHandles } from '../visual/handles';
import { isDiscoverJourney } from 'canvas/isDiscoverJourney';
import { Features } from '@ardoq/features';
import { hasFeature } from 'modelInterface/features/featureInterface';
import { getWorkspacegroupMenu } from 'contextMenus/workspacegroupMenu';
import {
  getBaseLayoutToolsMenuItems,
  getDropdownDivider,
} from 'contextMenus/menuItems';
import { GroupType } from '@ardoq/api-types';
import { t, type I18nLocale } from '@ardoq/i18n';
import { type ContextMenuAppState } from 'contextMenus/contextMenuRequested';

function* actionGroups(controller: Controller) {
  const set = new Set<BlocksViewNode>();

  for (const node of controller.selection.nodes()) {
    if (isGroup(node) && node.children.length > 1) {
      set.add(node);
    }
  }

  yield* set.size === 0 ? [controller.graph.root] : set.values();
}

// no idea why this needs to be exported
export const zoomToFitOnClick = (controller: Controller) => () => {
  controller.fit(
    controller.selection.size() === 0
      ? controller.graph.root.bounds
      : controller.selection.bounds(),
    true
  );
};

const zoomToFitConfig = (controller: Controller, i18nLocale: I18nLocale) =>
  layoutSettingViewActionButton({
    id: 'id_zoom_to_fit',
    label: t(i18nLocale, 'appLayout:viewPaneHeader.fit', 'Fit'),
    onClick: zoomToFitOnClick(controller),
    iconName: IconName.ZOOM_TO_FIT,
    onViewActionClick: controller.commands.onViewActionClick,
    viewActionName: 'zoomToFit',
  });

const realnodeContextMenuOptions = (
  controller: Controller,
  node: BlocksViewNode,
  appState: ContextMenuAppState
): DropdownItem[] => {
  // this should all be a separate function

  const contextNode = node; // pointerNode is a let variable that might change by the time a context menu option is clicked.
  const contextNodeIsGroup = isGroup(node);
  const group = contextNodeIsGroup ? node : node.parent!;
  const options: DropdownItem[] = [];
  const { open, modelId, groupType, isComponent: isComponentGroup } = group;

  const { canvas } = controller;
  const trackContextMenu = getTrackingFunction(
    canvas,
    TrackedContextMenus.NON_COMPONENT_GROUP_CONTEXT_MENU
  );

  const {
    i18nLocale,
    permissionContext,
    activeScenarioState,
    loadedWorkspaceIds,
    workspacesById,
  } = appState;

  const zoomToFitMenuItem = getZoomToFitContextMenuItem(
    () =>
      controller.fit(
        controller.selection.size() === 0
          ? contextNode.bounds
          : controller.selection.bounds(),
        true
      ),
    i18nLocale,
    trackContextMenu
  );

  const collapseGroupMenuItem = getExpandOrCollapseGroupMenuItem(
    open,
    i18nLocale,
    () => controller.setOpen(group, !open),
    trackContextMenu
  );

  // KFQ12026
  const isCanvasPrototype = hasFeature(Features.CANVAS_PROTOTYPE);
  if (!isCanvasPrototype) {
    options.push(zoomToFitMenuItem);
  }

  if (contextNodeIsGroup) {
    const positionTextMenuItem: DropdownHeader = {
      label: t(i18nLocale, 'contextMenus:position', 'Position'),
      type: DropdownOptionType.HEADER,
    };

    if (!isCanvasPrototype) {
      options.push(collapseGroupMenuItem);
    }

    if (contextNode.open && contextNode.children!.length > 1) {
      if (!isCanvasPrototype) {
        options.push({ type: DropdownOptionType.DIVIDER });
        options.push({
          type: DropdownOptionType.OPTION,
          label: t(
            i18nLocale,
            'appLayout:viewPaneHeader.applySmartLayout',
            'Apply Smart Layout'
          ),
          onClick: () => {
            trackContextMenu('smart layout');
            controller.resetLayout([contextNode], LayoutType.Smart);
          },
          iconName: IconName.VIEW_COMPACT,
        });

        options.push({
          type: DropdownOptionType.OPTION,
          label: t(
            i18nLocale,
            'appLayout:viewPaneHeader.applyGridLayout',
            'Apply Grid Layout'
          ),
          onClick: () => {
            trackContextMenu('grid layout');
            controller.resetLayout([contextNode], LayoutType.Grid);
          },
          iconName: IconName.VIEW_COMPACT,
        });
      }
      if (isComponentGroup || !isCanvasPrototype) {
        options.push(
          ...[
            ...(isCanvasPrototype ? [positionTextMenuItem] : []),
            ...getBaseLayoutToolsMenuItems(
              controller,
              contextNode,
              trackContextMenu,
              i18nLocale
            ),
            getDropdownDivider(),
          ]
        );
      }
    }

    if (groupType === GroupType.WORKSPACE && modelId) {
      if (isCanvasPrototype) {
        const workspacegroupMenuItems = getWorkspacegroupMenu(
          canvas,
          modelId,
          controller,
          contextNode,
          i18nLocale,
          permissionContext
        );
        options.push(...workspacegroupMenuItems);
      } else {
        const workspaces = [workspacesById[modelId]].filter(isWorkspace);
        const workspaceMenu = getWorkspaceMenu({
          event: null,
          target: canvas,
          x: 0,
          y: 0,
          ...toWorkspaceMenuState(activeScenarioState, loadedWorkspaceIds),
          workspaces,
          isViewpointMode: true,
          permissionContext,
          i18nLocale,
        });
        if (workspaceMenu?.length) {
          options.push(...workspaceMenu);
        }
      }
    }
  }

  const nodeContextMenu = nodeContextMenuOptions(
    controller,
    node,
    new Set<BlocksViewNode>(controller.selection.nodes()),
    appState
  ).reduce(transformComponentMenu, []);

  if (nodeContextMenu.length) {
    options.push(...nodeContextMenu);
  }

  return options;
};

const notifySelectionChange = (controller: Controller) => {
  const nodes = [...controller.selection.nodes()].filter(node => node.modelId);
  const graphSelection = nodes.map(node => node.modelId!);
  controller.commands.onSelectionChange(graphSelection);
};

/* and this is where the file contents really start */
enum InteractionState {
  Idle,
  Armed,
  Pan,
  Move,
  Resize,
  Reference, // create reference (not used)
  Marquee, // marquee select (not used)
}

// gets the view's cursor according to it's visual state
const getCursor = (
  state: InteractionState,
  node: BlocksViewNode | null,
  hit: NodeHit
) => {
  if (state === InteractionState.Pan) {
    return 'grabbing';
  }

  if (state === InteractionState.Armed) {
    // this will turn into either a move, a pan or a click
    return 'grab';
  }

  if (state === InteractionState.Move) {
    return 'move';
  }

  if (state === InteractionState.Resize) {
    switch (hit & NodeHit.Resize) {
      case NodeHit.Left:
      case NodeHit.Right:
        return 'ew-resize';

      case NodeHit.Top:
      case NodeHit.Bottom:
        return 'ns-resize';

      case NodeHit.LeftTop:
      case NodeHit.RightBottom:
        return 'nwse-resize';

      case NodeHit.RightTop:
      case NodeHit.LeftBottom:
        return 'nesw-resize';
    }
  }

  if (state === InteractionState.Idle && node) {
    if (hit & NodeHit.Url) {
      return 'pointer';
    }

    if (hit & NodeHit.Expand) {
      return 'pointer'; // mouse down now will expand/collapse without touching the selection
    }

    if (hit & NodeHit.Resize) {
      // pointer down will resize without touching the selection

      switch (hit & NodeHit.Resize) {
        case NodeHit.Left:
        case NodeHit.Right:
          return 'ew-resize';

        case NodeHit.Top:
        case NodeHit.Bottom:
          return 'ns-resize';

        case NodeHit.LeftTop:
        case NodeHit.RightBottom:
          return 'nwse-resize';

        case NodeHit.RightTop:
        case NodeHit.LeftBottom:
          return 'nesw-resize';
      }
    }

    return 'default';
  }

  return 'grab';
};

// sets the node and its entire descendancy's hidden visual state
const setHidden = (node: BlocksViewNode, hidden: boolean) => {
  if (hidden) {
    node.visualState |= BlocksViewVisualState.Hidden;
  } else {
    node.visualState &= ~BlocksViewVisualState.Hidden;
  }

  if (isGroup(node)) {
    for (const child of node.children) {
      setHidden(child, hidden);
    }
  }
};

export const createViewpointInteractions = (
  controller: Controller
): Interactions => {
  let interactionState: InteractionState = InteractionState.Idle;
  let pointerHit: NodeHit = NodeHit.None;

  const canvas = controller.canvas;
  const projection = controller.projection;
  const selection = controller.selection;
  const commands = controller.commands;
  const linksAggregation = controller.linksAggregation;
  const ctx = canvas.getContext('2d', { alpha: false })!;

  const hitTest: ClientModelHitTest = ({ clientX, clientY }) => {
    if (!controller.graph.root) {
      return null;
    }

    const { left: canvasLeft, top: canvasTop } = canvas.getBoundingClientRect();
    const viewportX = clientX - canvasLeft;
    const viewportY = clientY - canvasTop;

    return (
      hitTestGraphNodes(
        controller.graph.root,
        projection.toWorld([viewportX, viewportY])
      )?.modelId ?? null
    );
  };

  /** Where a node is drawn now, which pan and zoom both change. */
  const locate: ClientNodeLocator = nodeId => {
    if (!controller.graph.root) {
      return null;
    }
    const node = findDrawnGraphNode(controller.graph.root, nodeId);
    if (!node) {
      return null;
    }
    const [viewportX, viewportY] = projection.toViewport(
      rectCenter(node.bounds)
    );
    const { left: canvasLeft, top: canvasTop } = canvas.getBoundingClientRect();
    return { x: canvasLeft + viewportX, y: canvasTop + viewportY };
  };

  const attach = () => {
    const canvas = controller.canvas;

    canvas.addEventListener('pointerdown', onPointerDown);
    canvas.addEventListener('pointermove', onPointerMove);
    canvas.addEventListener('pointerup', onPointerUp);
    canvas.addEventListener('pointerleave', onPointerLeave);
    canvas.addEventListener('lostpointercapture', onLostPointerCapture);
    canvas.addEventListener('wheel', onWheel);
    canvas.addEventListener('keydown', onKeyDown);
    canvas.addEventListener('dblclick', onDoubleClick);
    canvas.addEventListener('contextmenu', onContextMenu);
    registerCanvasHitTest(controller.viewInstanceId, { hitTest, locate });
  };

  const detach = () => {
    const canvas = controller.canvas;

    canvas.removeEventListener('pointerdown', onPointerDown);
    canvas.removeEventListener('pointermove', onPointerMove);
    canvas.removeEventListener('pointerup', onPointerUp);
    canvas.removeEventListener('pointerleave', onPointerLeave);
    canvas.removeEventListener('lostpointercapture', onLostPointerCapture);
    canvas.removeEventListener('wheel', onWheel);
    canvas.removeEventListener('keydown', onKeyDown);
    canvas.removeEventListener('dblclick', onDoubleClick);
    canvas.removeEventListener('contextmenu', onContextMenu);
    unregisterCanvasHitTest(controller.viewInstanceId);
  };

  const renderCanvas = () => {
    /* other than the old edit interactions this completely generic */

    ctx.setTransform(projection.matrix());

    const done = renderGraph(
      controller.graph,
      projection.window(true),
      controller.getHighlight().size() !== 0 ? 'lowlight' : 'normal',
      ctx
    );

    if (
      (interactionState === InteractionState.Move ||
        interactionState === InteractionState.Resize) &&
      controller.editPreview &&
      controller.hover.node()
    ) {
      renderEditPreview(controller.editPreview, projection, ctx);
    }

    const hoverNode = controller.hover.node();

    if (interactionState === InteractionState.Idle && hoverNode) {
      renderHandles(hoverNode.bounds, ctx);

      // also draw alignment lines
    }

    return done;
  };

  const onPointerMove = (e: PointerEvent) => {
    if (!e.isPrimary) {
      /* ignore pointers that I don't care about */

      return;
    }

    controller.pointerDevice = [e.offsetX, e.offsetY];
    controller.pointerWorld = projection.toWorld(controller.pointerDevice);

    const hoverNode = controller.hover.node();

    if (interactionState === InteractionState.Idle) {
      pointerHit = updateHover(controller, hoverNode);
    }

    if (interactionState === InteractionState.Armed) {
      const slop = Math.hypot(
        controller.pointerDownDevice[0] - controller.pointerDevice[0],
        controller.pointerDownDevice[1] - controller.pointerDevice[1]
      );

      if (slop > 4 && hoverNode) {
        interactionState = InteractionState.Move;
        setHidden(hoverNode, true);
      }

      if (slop > 4 && !controller.hover.node()) {
        interactionState = InteractionState.Pan;
      }
    }

    if (interactionState === InteractionState.Pan) {
      controller.projection.adjustToPoint(
        controller.pointerDownWorld,
        controller.pointerDevice,
        false
      );
      controller.pointerWorld = controller.projection.toWorld(
        controller.pointerDevice
      );
      controller.renderCanvas();
    }

    if (interactionState === InteractionState.Move) {
      controller.editPreview = getMovePreview(
        controller.hover.node()!,
        pointerHit,
        controller.pointerDownWorld,
        controller.pointerWorld,
        projection.resolution(true)
      );
      controller.renderCanvas();
    }

    if (interactionState === InteractionState.Resize) {
      controller.editPreview = getResizePreview(
        controller.hover.node()!,
        pointerHit,
        controller.pointerDownWorld,
        controller.pointerWorld,
        projection.resolution(true)
      );
      controller.renderCanvas();
    }

    canvas.style.cursor = getCursor(
      interactionState,
      controller.hover.node(),
      pointerHit
    );
  };

  const onPointerDown = (e: PointerEvent) => {
    if (!e.isPrimary || e.button !== 0) {
      /* ignore pointers that I don't care about */

      return;
    }

    controller.pointerDevice = [e.offsetX, e.offsetY];
    controller.pointerWorld = projection.toWorld(controller.pointerDevice);
    pointerHit = updateHover(controller, controller.hover.node());
    controller.cancelTooltip();

    const hoverNode = controller.hover.node();

    if (hoverNode && pointerHit === NodeHit.Expand) {
      controller.setOpen(hoverNode, !hoverNode.open);
      controller.renderCanvas();
      canvas.style.cursor = getCursor(interactionState, hoverNode, pointerHit);
      return;
    }

    controller.pointerDownDevice = controller.pointerDevice;
    controller.pointerDownWorld = controller.pointerWorld;
    controller.pointerId = e.pointerId;
    controller.canvas.setPointerCapture(e.pointerId); // grab the pointer

    if (hoverNode && pointerHit & NodeHit.Resize) {
      // straight to resize state
      interactionState = InteractionState.Resize;
      controller.editPreview = getResizePreview(
        hoverNode,
        pointerHit,
        controller.pointerDownWorld,
        controller.pointerWorld,
        projection.resolution(true)
      );

      setHidden(hoverNode, true);
      canvas.style.cursor = getCursor(interactionState, hoverNode, pointerHit);
      controller.renderCanvas();
      return;
    }

    interactionState = InteractionState.Armed;
    canvas.style.cursor = getCursor(
      interactionState,
      controller.hover.node(),
      pointerHit
    );
  };

  const onPointerUp = (e: PointerEvent) => {
    if (!e.isPrimary || e.button !== 0) {
      /* ignore pointers that I don't care about */

      return;
    }

    const { hover, selection } = controller;
    const hoverNode = hover.node();

    if (
      interactionState === InteractionState.Armed &&
      pointerHit & NodeHit.Url
    ) {
      maybeDispatchUrlIconClick(hoverNode?.urlFieldValues, ViewIds.BLOCKS);
      interactionState = InteractionState.Idle;
      canvas.releasePointerCapture(controller.pointerId);
      canvas.style.cursor = getCursor(
        interactionState,
        controller.hover.node(),
        pointerHit
      );
      controller.renderCanvas();
      return;
    }

    if (interactionState === InteractionState.Armed) {
      if (controller.hover.node()) {
        /* click on a node */

        const isExtendedSelectModifier = isMac() ? e.metaKey : e.shiftKey;

        if (isExtendedSelectModifier) {
          if (hover.isSubsetOf(selection)) {
            for (const item of hover.values()) {
              selection.delete(item);
            }
          } else {
            for (const item of hover.values()) {
              selection.add(item);
            }
          }
        } else {
          selection.clear();

          for (const item of hover.values()) {
            selection.add(item);
          }
        }
      } else {
        /* click on background */

        selection.clear();
      }
      notifySelectionChange(controller);
      if (hoverNode?.modelId) {
        commands.updateDetailsDrawer(hoverNode.modelId);
      } else {
        commands.hideDetailsDrawer();
      }
    }

    if (interactionState === InteractionState.Pan) {
      controller.projection.adjustToPoint(
        controller.pointerDownWorld,
        controller.pointerDevice,
        false
      );
      controller.pointerWorld = controller.projection.toWorld(
        controller.pointerDevice
      );
    }

    if (
      interactionState === InteractionState.Move ||
      interactionState === InteractionState.Resize
    ) {
      if (controller.editPreview) {
        setHidden(controller.editPreview.node, false);

        forceNodeCell(
          controller.editPreview.node,
          controller.editPreview.bounds
        );
        editApply(controller.undoStack, controller.editPreview);
        applyLayout(controller.graph, linksAggregation);

        if (controller.editPreview.isGood) {
          controller.notifyLayoutChange();
        }

        controller.renderCanvas();
      }

      controller.editPreview = null;
    }

    interactionState = InteractionState.Idle;

    if (
      !isNaN(controller.pointerId) &&
      canvas.hasPointerCapture(controller.pointerId)
    ) {
      canvas.releasePointerCapture(controller.pointerId);
      controller.pointerId = NaN;
    }

    canvas.style.cursor = getCursor(
      interactionState,
      controller.hover.node(),
      pointerHit
    );
    controller.renderCanvas();
  };

  // this is completely generic and could be moved out
  const onPointerLeave = (e: PointerEvent) => {
    if (!e.isPrimary) {
      /* ignore pointers that I don't care about */

      return;
    }

    controller.pointerDevice = [NaN, NaN];
    controller.pointerWorld = [NaN, NaN];
    controller.scheduleTooltipCancel();
  };

  // this is almost completely generic and could be moved out
  const onLostPointerCapture = (e: PointerEvent) => {
    if (!e.isPrimary) {
      /* ignore pointers that I don't care about */

      return;
    }

    switch (interactionState) {
      case InteractionState.Idle:
        break;

      case InteractionState.Armed:
        // disarm
        break;

      case InteractionState.Pan:
        // put view back to original position
        break;

      case InteractionState.Move:
        if (controller.editPreview) {
          setHidden(controller.editPreview.node!, false);
          controller.editPreview = null;
        }

        break;

      case InteractionState.Resize:
        if (controller.editPreview) {
          setHidden(controller.editPreview.node!, false);
          controller.editPreview = null;
        }
        break;
    }

    controller.pointerId = NaN;
    interactionState = InteractionState.Idle;
    controller.renderCanvas();
    canvas.style.cursor = getCursor(
      interactionState,
      controller.hover.node(),
      pointerHit
    );
  };

  // this is completely generic and could be moved out
  const onKeyDown = (e: KeyboardEvent) => {
    let isHandled = true;
    const rc = projection.window(false);
    const speed = e.shiftKey ? 3 : 1;
    const isMenuModifier = isMac() ? e.metaKey : e.ctrlKey;
    const hoverNode = controller.hover.node();

    switch (e.key) {
      case 'PageUp':
        controller.zoomTo(rectCenter(rc), 1.1 * speed, true);
        break;

      case 'PageDown':
        controller.zoomTo(rectCenter(rc), 0.9 / speed, true);
        break;

      case 'ArrowLeft':
        controller.panBy([speed * 0.05 * rectWidth(rc), 0]);
        break;

      case 'ArrowUp':
        controller.panBy([0, speed * 0.05 * rectHeight(rc)]);
        break;

      case 'ArrowRight':
        controller.panBy([-speed * 0.05 * rectWidth(rc), 0]);
        break;

      case 'ArrowDown':
        controller.panBy([0, -speed * 0.05 * rectHeight(rc)]);
        break;

      case 'Enter': //
        if (e.shiftKey && selection.size() !== 0) {
          controller.fit(selection.bounds(), true);
        } else {
          controller.fit(controller.graph.root.bounds, true);
        }
        break;

      case ' ':
        if (hoverNode && isGroup(hoverNode)) {
          controller.setOpen(hoverNode, !hoverNode.open);
        } else {
          // this should be a controller action

          let count = 0;
          const groups = [];

          for (const node of selection.nodes()) {
            if (isGroup(node)) {
              groups.push(node);
              count += node.open ? 1 : -1;
            }
          }

          if (groups.length > 0) {
            for (const group of groups) {
              group.open = count < 0;
            }

            applyLayout(controller.graph, linksAggregation);
            renderCanvas();
          }
        }

        break;

      case 'z':
        if (isMenuModifier) {
          controller.undo();
        } else {
          isHandled = false;
        }

        break;
      /*
      case 'k':
        if (focusNode) {
          controller.setPreviewLink(focusNode, focusNode);
        } else {
          isHandled = false;
        }
        break;


      case 'f': // find
        if (e.ctrlKey) {
          // show the find control
        } else {
          isHandled = false;
        }

        break;
      */

      /*
      case 'Delete': // delete selected node or relationship
        isHandled = false;
        break;
      */

      case 'Escape':
        controller.cancelTooltip();

        if (
          !isNaN(controller.pointerId) &&
          canvas.hasPointerCapture(controller.pointerId)
        ) {
          canvas.releasePointerCapture(controller.pointerId);
        }
        break;

      default:
        isHandled = false;
    }

    if (isHandled) {
      controller.pointerWorld = projection.toWorld(controller.pointerDevice); // recalculate the world position just in case the projection got changed
      controller.cancelTooltip();
      controller.renderCanvas(); // some of the events will already have done this, but rendering twice is a free operation
      e.stopPropagation();
      e.stopImmediatePropagation();
      e.preventDefault();
    }
  };

  // this is completely generic and could be moved out
  const onWheel = (e: WheelEvent) => {
    const speed = e.ctrlKey ? 4 : 1;
    const deltaY = (speed * clamp(e.deltaY, -800, 800)) / 1000;

    e.stopPropagation();
    e.stopImmediatePropagation();
    e.preventDefault();

    controller.pointerDevice = [e.offsetX, e.offsetY];
    controller.pointerWorld = projection.toWorld(controller.pointerDevice);
    controller.cancelTooltip();
    controller.zoomTo(controller.pointerWorld, 1 - deltaY, false);
  };

  // this is almost completely generic and could be moved out
  const onDoubleClick = (e: MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    const hoverNode = controller.hover.node();

    if (hoverNode) {
      const modelIds = [];

      if (selection.has(hoverNode)) {
        for (const node of selection.nodes()) {
          if (node.modelId) {
            modelIds.push(node.modelId);
          }
        }
      }

      if (!selection.has(hoverNode) && hoverNode.modelId) {
        modelIds.push(hoverNode.modelId);
      }

      if (hoverNode.modelId) {
        commands.onComponentDoubleClick(hoverNode.modelId, modelIds);
      }
    }
  };

  // this is completely generic and could be moved out
  const onContextMenu = (e: MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    commands.openContextMenu({
      position: { left: e.clientX, top: e.clientY },
      getItems: contextMenuOptions,
      onOpened: () => controller.cancelTooltip(),
    });

    /* tinker with the selection */

    const { selection, hover } = controller;

    if (!hover.isSubsetOf(selection)) {
      selection.clear();
      for (const item of hover.values()) {
        selection.add(item);
      }
    }
    notifySelectionChange(controller);
    controller.renderCanvas();
  };

  const contextMenuOptions = (
    appState: ContextMenuAppState
  ): DropdownItem[] => {
    const pointerLink = controller.hover.link();
    const pointerNode = controller.hover.node();

    if (isDiscoverJourney()) {
      return [];
    }

    if (pointerLink) {
      return linkContextMenuOptions(controller, pointerLink, appState);
    }

    if (pointerNode) {
      return realnodeContextMenuOptions(controller, pointerNode, appState);
    }

    if (!pointerLink && !pointerNode) {
      return rootContextMenuOptions(controller);
    }

    return [];
  };

  const { onViewActionClick } = commands;

  const viewActionsConfigs = (i18nLocale: I18nLocale): SettingsConfig[] => {
    const hasGroups = controller.graph.root.children?.some(isGroup);
    const configs: SettingsConfig[] = [];
    /*
    configs.push({
      id: 'id_editmode',
      type: SettingsType.TOGGLE,
      label: 'Edit mode',
      onClick: () => controller.setInteractionMode(InteractionMode.View),
      iconName: IconName.EDIT,
      isDisabled: false,
    });
*/
    configs.push({
      id: 'id_undo',
      type: SettingsType.BUTTON,
      label: t(i18nLocale, 'appLayout:viewPaneHeader.undo', 'Undo'),
      onClick: () => controller.undo(),
      iconName: IconName.UNDO,
      isDisabled: false,
    });

    configs.push({
      id: 'id_reset_layout',
      type: SettingsType.DROPDOWN,
      label: t(i18nLocale, 'appLayout:viewPaneHeader.apply', 'Apply'),
      iconName: IconName.VIEW_COMPACT,
      options: [
        layoutSettingViewActionOption({
          label: t(
            i18nLocale,
            'appLayout:viewPaneHeader.applySmartLayout',
            'Apply Smart Layout'
          ),
          onClick: () =>
            controller.resetLayout(actionGroups(controller), LayoutType.Smart),
          onViewActionClick,
          viewActionName: 'applyLayout',
          viewActionValue: LayoutType.Smart,
        }),
        layoutSettingViewActionOption({
          label: t(
            i18nLocale,
            'appLayout:viewPaneHeader.applyGridLayout',
            'Apply Grid Layout'
          ),
          onClick: () =>
            controller.resetLayout(actionGroups(controller), LayoutType.Grid),
          onViewActionClick,
          viewActionName: 'applyLayout',
          viewActionValue: LayoutType.Grid,
        }),
      ],
    } satisfies DropdownConfig);

    configs.push({ id: 'id_divider1', type: SettingsType.DIVIDER });

    configs.push(
      layoutSettingViewActionButton({
        id: 'id_rotate',
        label: t(i18nLocale, 'appLayout:viewPaneHeader.rotate', 'Rotate'),
        onClick: () => {
          controller.rotate(actionGroups(controller));
        },
        iconName: IconName.ROTATE,
        onViewActionClick,
        viewActionName: 'rotate',
      })
    );

    configs.push(
      layoutSettingViewActionButton({
        id: 'id_flip_h',
        label: t(
          i18nLocale,
          'appLayout:viewPaneHeader.flipHorizontally',
          'Flip Horizontally'
        ),
        onClick: () => {
          controller.flipHorizontal(actionGroups(controller));
        },
        iconName: IconName.FLIP_HORIZONTALLY,
        onViewActionClick,
        viewActionName: 'flipHorizontally',
      })
    );

    configs.push(
      layoutSettingViewActionButton({
        id: 'id_flip_v',
        label: t(
          i18nLocale,
          'appLayout:viewPaneHeader.flipVertically',
          'Flip Vertically'
        ),
        onClick: () => {
          controller.flipVertical(actionGroups(controller));
        },
        iconName: IconName.FLIP_VERTICALLY,
        onViewActionClick,
        viewActionName: 'flipVertically',
      })
    );

    configs.push({ id: 'id_divider2', type: SettingsType.DIVIDER });

    configs.push(
      layoutSettingViewActionButton({
        id: 'id_collapse_all',
        label: t(
          i18nLocale,
          'appLayout:viewPaneHeader.collapseAll',
          'Collapse all'
        ),
        onClick: controller.collapseAll,
        iconName: IconName.COLLAPSE_ALL,
        isDisabled: !hasGroups,
        onViewActionClick,
        viewActionName: 'collapseAll',
      })
    );

    configs.push(
      layoutSettingViewActionButton({
        id: 'id_expand_all',
        label: t(
          i18nLocale,
          'appLayout:viewPaneHeader.expandAll',
          'Expand all'
        ),
        onClick: controller.expandAll,
        iconName: IconName.EXPAND_ALL,
        isDisabled: !hasGroups,
        onViewActionClick,
        viewActionName: 'expandAll',
      })
    );

    configs.push({ id: 'id_divider3', type: SettingsType.DIVIDER });

    configs.push(
      layoutSettingViewActionButton({
        id: 'id_zoom_in',
        label: t(i18nLocale, 'appLayout:viewPaneHeader.zoomIn', 'Zoom in'),
        onClick: controller.zoomIn,
        iconName: IconName.ZOOM_IN,
        isDisabled: false,
        onViewActionClick,
        viewActionName: 'zoomIn',
      })
    );

    configs.push(
      layoutSettingViewActionButton({
        id: 'id_zoom_out',
        label: t(i18nLocale, 'appLayout:viewPaneHeader.zoomOut', 'Zoom out'),
        onClick: controller.zoomOut,
        iconName: IconName.ZOOM_OUT,
        onViewActionClick,
        viewActionName: 'zoomOut',
      })
    );

    configs.push(zoomToFitConfig(controller, i18nLocale));

    return configs;
  };

  return {
    attach,
    detach: detach,
    renderCanvas,
    viewActionsConfigs: viewActionsConfigs,
  };
};
